import React, { useEffect, useState } from "react";
import {
  Plus,
  Wallet,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  RefreshCw,
  Send,
  Download,
  AlertTriangle,
  CheckCircle,
  Copy,
  ExternalLink,
  Shield,
  Key,
  MoreHorizontal,
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { toast } from "sonner";
import { AdminWallet } from "../../App";
import {
  useGetWalletsApiCallQuery,
  useUpdateWalletApiCallMutation,
} from "../../services/api/wallets/wallets.api";
import { WalletFormType } from "../../services/api/wallets/wallets.type";
import { set } from "react-hook-form";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { ApiError } from "../../services/api/auth/auth.type";

// Mock wallet data
const mockWallets: AdminWallet[] = [
  {
    id: "1",
    name: "Primary Distribution Wallet",
    address: "0x9876543210fedcba9876543210fedcba98765432",
    isActive: true,
    balance: 245000,
    tokenSymbol: "PXT",
    lastUsed: "2024-01-28T10:30:00Z",
    createdDate: "2024-01-01T00:00:00Z",
    totalDistributed: 755000,
    totalReceived: 1000000,
    transactionCount: 1234,
  },
  {
    id: "2",
    name: "Secondary Backup Wallet",
    address: "0x1111222233334444555566667777888899990000",
    isActive: false,
    balance: 50000,
    tokenSymbol: "PXT",
    lastUsed: "2024-01-25T14:20:00Z",
    createdDate: "2024-01-10T00:00:00Z",
    totalDistributed: 25000,
    totalReceived: 75000,
    transactionCount: 156,
  },
  {
    id: "3",
    name: "GWT Token Wallet",
    address: "0xaaaaaabbbbbbccccccddddddeeeeeeffffffffff",
    isActive: true,
    balance: 125000,
    tokenSymbol: "GWT",
    lastUsed: "2024-01-27T16:45:00Z",
    createdDate: "2024-01-15T00:00:00Z",
    totalDistributed: 375000,
    totalReceived: 500000,
    transactionCount: 678,
  },
];

const WalletManagement: React.FC = () => {
  const [wallets, setWallets] = useState<AdminWallet[]>(mockWallets);
  const [selectedWallet, setSelectedWallet] = useState<AdminWallet | null>(
    null
  );
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showFundDialog, setShowFundDialog] = useState(false);
  const [fundingAmount, setFundingAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [newWalletForm, setNewWalletForm] = useState({
    name: "",
    tokenSymbol: "",
    privateKey: "",
    generateNew: true,
  });

  const [showSecret, setShowSecret] = useState(false);
  const [walletFormData, setWalletFormData] = useState<WalletFormType>({
    walletAddress: "",
    privateKey: "",
  });
  const [isEdit, setIsEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { data: walletsData } = useGetWalletsApiCallQuery();
  const [updateWalletApiCall] = useUpdateWalletApiCallMutation();

  const handleInputChnage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEdit(true);
    const { name, value } = e.target;
    setWalletFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWalletFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      const response = await updateWalletApiCall(walletFormData).unwrap();
      if (response.responseCode === 200) {
        toast.success(response.responseMessage);
      }
    } catch (error) {
      const fetchError = error as FetchBaseQueryError;
      const errorData = fetchError.data as ApiError;
      toast.error(errorData?.responseMessage || "Error updating wallet");
    } finally {
      setIsLoading(false);
    }
  };

  const formatWalletAddress = (address: string) => {
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const handleRefreshBalance = async (walletId: string) => {
    // Simulate API call to refresh balance
    toast.success("Wallet balance refreshed");
  };

  const handleCreateWallet = () => {
    if (!newWalletForm.name || !newWalletForm.tokenSymbol) {
      toast.error("Please fill in all required fields");
      return;
    }

    const newWallet: AdminWallet = {
      id: Date.now().toString(),
      name: newWalletForm.name,
      address: newWalletForm.generateNew
        ? "0x" + Math.random().toString(16).substr(2, 40)
        : "0x1234567890abcdef1234567890abcdef12345678",
      isActive: false,
      balance: 0,
      tokenSymbol: newWalletForm.tokenSymbol,
      lastUsed: "Never",
      createdDate: new Date().toISOString(),
      totalDistributed: 0,
      totalReceived: 0,
      transactionCount: 0,
    };

    setWallets((prev) => [...prev, newWallet]);
    setNewWalletForm({
      name: "",
      tokenSymbol: "",
      privateKey: "",
      generateNew: true,
    });
    setShowCreateDialog(false);
    toast.success("Wallet created successfully");
  };

  const handleFundWallet = () => {
    if (!selectedWallet || !fundingAmount) {
      toast.error("Please enter a valid amount");
      return;
    }

    const amount = parseFloat(fundingAmount);
    setWallets((prev) =>
      prev.map((wallet) =>
        wallet.id === selectedWallet.id
          ? {
              ...wallet,
              balance: wallet.balance + amount,
              totalReceived: wallet.totalReceived + amount,
              transactionCount: wallet.transactionCount + 1,
              lastUsed: new Date().toISOString(),
            }
          : wallet
      )
    );

    setFundingAmount("");
    setShowFundDialog(false);
    toast.success(
      `Wallet funded with ${amount.toLocaleString()} ${
        selectedWallet.tokenSymbol
      }`
    );
  };

  const handleWithdrawFunds = () => {
    if (!selectedWallet || !withdrawAmount) {
      toast.error("Please enter a valid amount");
      return;
    }

    const amount = parseFloat(withdrawAmount);
    if (amount > selectedWallet.balance) {
      toast.error("Insufficient balance");
      return;
    }

    setWallets((prev) =>
      prev.map((wallet) =>
        wallet.id === selectedWallet.id
          ? {
              ...wallet,
              balance: wallet.balance - amount,
              transactionCount: wallet.transactionCount + 1,
              lastUsed: new Date().toISOString(),
            }
          : wallet
      )
    );

    setWithdrawAmount("");
    toast.success(
      `Withdrew ${amount.toLocaleString()} ${selectedWallet.tokenSymbol}`
    );
  };

  const handleToggleWallet = (walletId: string) => {
    setWallets((prev) =>
      prev.map((wallet) => {
        if (wallet.id === walletId) {
          return { ...wallet, isActive: !wallet.isActive };
        }
        // If activating this wallet, deactivate others with same token
        if (wallet.id !== walletId && wallet.isActive) {
          const targetWallet = prev.find((w) => w.id === walletId);
          if (targetWallet && wallet.tokenSymbol === targetWallet.tokenSymbol) {
            return { ...wallet, isActive: false };
          }
        }
        return wallet;
      })
    );
    toast.success("Wallet status updated");
  };

  const handleDeleteWallet = (walletId: string) => {
    setWallets((prev) => prev.filter((wallet) => wallet.id !== walletId));
    toast.success("Wallet deleted successfully");
  };

  const getTotalBalance = () => {
    return wallets.reduce((sum, wallet) => {
      // Convert all to USD equivalent for demo (assuming 1 token = 1 USD)
      return sum + wallet.balance;
    }, 0);
  };

  const getActiveWallets = () => {
    return wallets.filter((wallet) => wallet.isActive).length;
  };

  useEffect(() => {
    if (walletsData && walletsData?.result) {
      setWalletFormData((prev) => ({
        ...prev,
        walletAddress: walletsData?.result?.walletAddress || "",
        privateKey: walletsData?.result?.privateKey || "",
      }));
    }
  }, [walletsData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            Wallet Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage admin wallets and funding for token distribution
          </p>
        </div>

        {/* <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700">
              <Plus className="h-4 w-4 mr-2" />
              Add Wallet
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Wallet</DialogTitle>
              <DialogDescription>
                Add a new admin wallet for token distribution
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="walletName">Wallet Name</Label>
                <Input
                  id="walletName"
                  placeholder="e.g., Primary Distribution Wallet"
                  value={newWalletForm.name}
                  onChange={(e) =>
                    setNewWalletForm((prev) => ({
                      ...prev,
                      name: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tokenSymbol">Token Symbol</Label>
                <Input
                  id="tokenSymbol"
                  placeholder="e.g., PXT"
                  value={newWalletForm.tokenSymbol}
                  onChange={(e) =>
                    setNewWalletForm((prev) => ({
                      ...prev,
                      tokenSymbol: e.target.value,
                    }))
                  }
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label>Wallet Creation Method</Label>
                  <Switch
                    checked={newWalletForm.generateNew}
                    onCheckedChange={(checked) =>
                      setNewWalletForm((prev) => ({
                        ...prev,
                        generateNew: checked,
                      }))
                    }
                  />
                </div>

                {newWalletForm.generateNew ? (
                  <div className="p-4 bg-green-50 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-green-900">
                          Generate New Wallet
                        </h4>
                        <p className="text-sm text-green-700">
                          A new wallet address and private key will be generated
                          securely
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="privateKey">Private Key</Label>
                    <Input
                      id="privateKey"
                      type="password"
                      placeholder="Enter existing private key"
                      value={newWalletForm.privateKey}
                      onChange={(e) =>
                        setNewWalletForm((prev) => ({
                          ...prev,
                          privateKey: e.target.value,
                        }))
                      }
                    />
                    <p className="text-sm text-red-600">
                      ⚠️ Never share your private keys. Store them securely.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowCreateDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleCreateWallet}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create Wallet
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog> */}
      </div>

      <form
        onSubmit={handleWalletFormSubmit}
        className="space-y-4 border shadow rounded-lg p-4"
      >
        {/* Wallet Address */}
        <div className="space-y-2">
          <Label htmlFor="walletAddress">Wallet Address</Label>
          <Input
            id="walletAddress"
            placeholder="e.g., 0x1234abcd..."
            value={walletFormData?.walletAddress}
            name="walletAddress"
            // onChange={(e) =>
            //   setNewWalletForm((prev) => ({
            //     ...prev,
            //     walletAddress: e.target.value,
            //   }))
            // }
            onChange={handleInputChnage}
          />
        </div>

        {/* Secret Key */}
        <div className="space-y-2">
          <Label htmlFor="secretKey">Secret Key</Label>
          <div className="relative">
            <Input
              id="secretKey"
              type={showSecret ? "text" : "password"}
              placeholder="Enter your secret key"
              value={walletFormData?.privateKey}
              name="privateKey"
              // onChange={(e) =>
              //   setNewWalletForm((prev) => ({
              //     ...prev,
              //     secretKey: e.target.value,
              //   }))
              // }
              onChange={handleInputChnage}
            />
            <button
              type="button"
              onClick={() => setShowSecret((prev) => !prev)}
              className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
            >
              {showSecret ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="text-sm text-red-600">
            ⚠️ Never share your secret key. Store it securely.
          </p>
        </div>

        {/* Update Button */}
        <div className="pt-2">
          <Button
            disabled={!isEdit || isLoading}
            type="submit"
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Update{" "}
            {isLoading && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
            )}
          </Button>
        </div>
      </form>

      {/* Stats Overview */}
      {/* <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Wallets</p>
                <p className="text-2xl font-semibold">{wallets.length}</p>
              </div>
              <Wallet className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Active Wallets</p>
                <p className="text-2xl font-semibold text-green-600">
                  {getActiveWallets()}
                </p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Balance</p>
                <p className="text-2xl font-semibold">
                  {getTotalBalance().toLocaleString()}
                </p>
              </div>
              <div className="text-purple-600 text-2xl font-bold">💎</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Low Balance</p>
                <p className="text-2xl font-semibold text-red-600">
                  {wallets.filter((w) => w.balance < 10000).length}
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>
      </div> */}

      {/* Wallets Table */}
      {/* <Card>
        <CardHeader>
          <CardTitle>Admin Wallets</CardTitle>
          <CardDescription>
            Manage your distribution wallets and monitor balances
          </CardDescription>
        </CardHeader>

        <CardContent>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Wallet</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Balance</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Used</TableHead>
                  <TableHead>Transactions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {wallets.map((wallet) => (
                  <TableRow key={wallet.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium text-gray-900">
                          {wallet.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {wallet.tokenSymbol} Wallet
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm">
                          {formatWalletAddress(wallet.address)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(wallet.address)}
                        >
                          <Copy className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-semibold">
                          {wallet.balance.toLocaleString()} {wallet.tokenSymbol}
                        </div>
                        {wallet.balance < 10000 && (
                          <Badge className="bg-red-100 text-red-800 text-xs mt-1">
                            Low Balance
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={
                          wallet.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-800"
                        }
                      >
                        {wallet.isActive ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {wallet.lastUsed === "Never" ? (
                          <span className="text-gray-500">Never</span>
                        ) : (
                          new Date(wallet.lastUsed).toLocaleDateString()
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">
                          {wallet.transactionCount}
                        </div>
                        <div className="text-gray-500">transactions</div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => handleRefreshBalance(wallet.id)}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Refresh Balance
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedWallet(wallet);
                              setShowFundDialog(true);
                            }}
                          >
                            <Send className="h-4 w-4 mr-2" />
                            Fund Wallet
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleToggleWallet(wallet.id)}
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            {wallet.isActive ? "Deactivate" : "Activate"}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() =>
                              window.open(
                                `https://etherscan.io/address/${wallet.address}`,
                                "_blank"
                              )
                            }
                          >
                            <ExternalLink className="h-4 w-4 mr-2" />
                            View on Etherscan
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <DropdownMenuItem
                                onSelect={(e) => e.preventDefault()}
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Wallet
                              </DropdownMenuItem>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Delete Wallet
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to delete this wallet?
                                  This action cannot be undone. Make sure to
                                  withdraw all funds first.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDeleteWallet(wallet.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {wallets.length === 0 && (
            <div className="text-center py-12">
              <Wallet className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No wallets created
              </h3>
              <p className="text-gray-500 mb-4">
                Create your first admin wallet to start managing token
                distributions
              </p>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Your First Wallet
              </Button>
            </div>
          )}
        </CardContent>
      </Card> */}

      {/* Fund Wallet Dialog */}
      {/* <Dialog open={showFundDialog} onOpenChange={setShowFundDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fund Wallet</DialogTitle>
            <DialogDescription>
              Add funds to {selectedWallet?.name}
            </DialogDescription>
          </DialogHeader>

          {selectedWallet && (
            <Tabs defaultValue="deposit" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="deposit">Deposit</TabsTrigger>
                <TabsTrigger value="withdraw">Withdraw</TabsTrigger>
              </TabsList>

              <TabsContent value="deposit" className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Current Balance</span>
                    <span className="font-semibold">
                      {selectedWallet.balance.toLocaleString()}{" "}
                      {selectedWallet.tokenSymbol}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fundAmount">Deposit Amount</Label>
                  <Input
                    id="fundAmount"
                    type="number"
                    placeholder="Enter amount to deposit"
                    value={fundingAmount}
                    onChange={(e) => setFundingAmount(e.target.value)}
                  />
                </div>

                <Button
                  onClick={handleFundWallet}
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={!fundingAmount}
                >
                  <Send className="h-4 w-4 mr-2" />
                  Deposit Funds
                </Button>
              </TabsContent>

              <TabsContent value="withdraw" className="space-y-4">
                <div className="p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      Available Balance
                    </span>
                    <span className="font-semibold">
                      {selectedWallet.balance.toLocaleString()}{" "}
                      {selectedWallet.tokenSymbol}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="withdrawAmount">Withdraw Amount</Label>
                  <Input
                    id="withdrawAmount"
                    type="number"
                    placeholder="Enter amount to withdraw"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    max={selectedWallet.balance}
                  />
                </div>

                <Button
                  onClick={handleWithdrawFunds}
                  className="w-full bg-orange-600 hover:bg-orange-700"
                  disabled={
                    !withdrawAmount ||
                    parseFloat(withdrawAmount) > selectedWallet.balance
                  }
                >
                  <Download className="h-4 w-4 mr-2" />
                  Withdraw Funds
                </Button>
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog> */}

      {/* Security Notice */}
      <Card className="border-amber-200 bg-amber-50">
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <Shield className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <h4 className="font-medium text-amber-900 mb-1">
                Security Reminder
              </h4>
              <ul className="text-sm text-amber-800 space-y-1">
                <li>
                  • Always keep your private keys secure and never share them
                </li>
                <li>• Use hardware wallets for large amounts</li>
                <li>• Regularly backup your wallet data</li>
                <li>• Monitor wallet balances and transactions regularly</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default WalletManagement;
